import React, { type ComponentType, type CSSProperties } from 'react';
import { useBrand } from '../../../app/config/brand';
import { Icons } from '../../../shared/icons/Icons';

interface IconProps {
  className?: string;
  style?: CSSProperties;
}

interface TileProps {
  icon: ComponentType<IconProps>;
  label: string;
  count?: number;
  onClick: () => void;
  primary?: boolean;
  testId: string;
}

const Tile: React.FC<TileProps> = ({ icon: Icon, label, count, onClick, primary, testId }) => {
  const brand = useBrand();

  if (primary) {
    return (
      <button
        type="button"
        data-testid={testId}
        onClick={onClick}
        className="flex flex-col items-start justify-between rounded-xl p-4 sm:p-5 min-h-32 text-white shadow-md transition-all hover:shadow-lg hover:scale-[1.02]"
        style={{
          background: `linear-gradient(135deg, ${brand.colors.secondary} 0%, ${brand.colors.secondaryLight} 100%)`,
        }}
      >
        <div className="w-10 h-10 rounded-lg bg-white/15 flex items-center justify-center">
          <Icon className="w-6 h-6" />
        </div>
        <div className="mt-3 text-left">
          <div className="text-xs uppercase tracking-wide font-medium opacity-80">Quick action</div>
          <div className="text-lg font-semibold leading-tight">{label}</div>
        </div>
      </button>
    );
  }

  return (
    <button
      type="button"
      data-testid={testId}
      onClick={onClick}
      className="flex flex-col items-start justify-between rounded-xl p-4 sm:p-5 min-h-32 bg-white border shadow-xs transition-all hover:shadow-md hover:scale-[1.02] text-left"
      style={{ borderColor: brand.colors.border }}
    >
      <div
        className="w-10 h-10 rounded-lg flex items-center justify-center"
        style={{ backgroundColor: brand.colors.primaryLight, color: brand.colors.secondary }}
      >
        <Icon className="w-6 h-6" />
      </div>
      <div className="mt-3 flex items-center justify-between w-full gap-2">
        <div className="text-base font-semibold" style={{ color: brand.colors.secondary }}>
          {label}
        </div>
        {count !== undefined && count > 0 && (
          <span
            data-testid={`${testId}-badge`}
            className="inline-flex items-center justify-center min-w-6 h-6 px-2 rounded-full text-xs font-bold text-white"
            style={{ backgroundColor: brand.colors.secondary }}
          >
            {count > 99 ? '99+' : count}
          </span>
        )}
      </div>
    </button>
  );
};

interface QuickActionTilesProps {
  onNavigate: (path: string) => void;
  casesCount?: number;
  pendingOrdersCount?: number;
}

const QuickActionTiles: React.FC<QuickActionTilesProps> = ({
  onNavigate,
  casesCount,
  pendingOrdersCount,
}) => {
  return (
    <div
      data-testid="quick-action-tiles"
      className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 sm:gap-4 mb-6"
    >
      <Tile
        testId="quick-action-place-order"
        icon={Icons.Plus}
        label="Place an Order"
        primary
        onClick={() => onNavigate('orders/new')}
      />
      <Tile
        testId="quick-action-manage-cases"
        icon={Icons.Folder}
        label="Manage Cases"
        count={casesCount}
        onClick={() => onNavigate('cases')}
      />
      <Tile
        testId="quick-action-pending-orders"
        icon={Icons.Clock}
        label="Pending Orders"
        count={pendingOrdersCount}
        onClick={() => onNavigate('orders?status=pending')}
      />
      <Tile
        testId="quick-action-closed-orders"
        icon={Icons.Check}
        label="Closed Orders"
        onClick={() => onNavigate('orders?status=closed')}
      />
      <Tile
        testId="quick-action-reports"
        icon={Icons.Chart}
        label="Reports"
        onClick={() => onNavigate('reports')}
      />
    </div>
  );
};

export default QuickActionTiles;
